import PropTypes from "prop-types";
import styled from "styled-components";
import { FaPrint, FaShare, FaWindows, FaApple, FaAndroid } from "react-icons/fa";
import ContentLayout from "../../../layout/ContentLayout";
import { H2, H3, Paragraph } from "../../../styles/AppStyles";
import { LINKS, href } from "../../../config/links";

const CalloutBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  border-left: 5px solid ${({ $type, theme }) => {
    if ($type === "do") return "#10b981";
    if ($type === "dont") return "#ef4444";
    if ($type === "warning") return "#f59e0b";
    if ($type === "tip") return "#6366f1";
    return theme.iconColor;
  }};
  background-color: ${({ $type, theme }) => {
    if ($type === "do") return "rgba(16, 185, 129, 0.08)";
    if ($type === "dont") return "rgba(239, 68, 68, 0.08)";
    if ($type === "warning") return "rgba(245, 158, 11, 0.08)";
    if ($type === "tip") return "rgba(99, 102, 241, 0.08)";
    return "rgba(99, 102, 241, 0.08)";
  }};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  strong {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    color: ${({ $type }) => {
      if ($type === "do") return "#10b981";
      if ($type === "dont") return "#ef4444";
      if ($type === "warning") return "#f59e0b";
      if ($type === "tip") return "#6366f1";
      return "#6366f1";
    }};
  }

  ul {
    margin: 0.75rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
`;

const PlatformGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.25rem;
  margin: 1.5rem 0;
`;

const PlatformCard = styled.div`
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 2px solid ${({ theme }) => theme.iconColor};
  border-radius: 8px;
  color: ${({ theme }) => theme.text};

  h4 {
    margin-top: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: ${({ theme }) => theme.iconColor};
  }

  ol {
    margin: 0.5rem 0 0 0;
    padding-left: 1.25rem;

    li {
      margin-bottom: 0.45rem;
      line-height: 1.5;
      font-size: 0.93rem;
    }
  }
`;

const BeforeAfterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  margin: 1.5rem 0;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StateCard = styled.div`
  padding: 1.25rem;
  border-radius: 8px;
  border: 2px solid ${({ $after }) => ($after ? "#10b981" : "#ef4444")};
  background-color: ${({ $after }) =>
    $after ? "rgba(16, 185, 129, 0.06)" : "rgba(239, 68, 68, 0.06)"};
  color: ${({ theme }) => theme.text};

  h4 {
    margin-top: 0;
    color: ${({ $after }) => ($after ? "#10b981" : "#ef4444")};
  }

  ul {
    margin: 0.5rem 0 0 0;
    padding-left: 1.25rem;

    li {
      margin-bottom: 0.4rem;
      line-height: 1.5;
      font-size: 0.93rem;
    }
  }
`;

const ChecklistSection = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.iconColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  h3 {
    margin-top: 0;
  }
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  input[type="checkbox"] {
    margin-top: 0.35rem;
    flex-shrink: 0;
    width: 1.1rem;
    height: 1.1rem;
    cursor: pointer;
  }

  label {
    flex: 1;
    cursor: pointer;
    line-height: 1.6;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;

    button,
    a {
      width: 100%;
    }
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background-color: ${({ theme }) => theme.iconColor};
  color: ${({ theme }) => theme.background};
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const DeviceSecurityGuide = ({ istouch, isdarkmode }) => {
  const canonicalUrl = href(LINKS.QUICK_GUIDES.DEVICE_SECURITY);

  // const heroImageSrc = "/images/guides/device-security.webp";

  return (
    <ContentLayout
        istouch={istouch}
        isdarkmode={isdarkmode}
        displayTitle="Harden Your Computer & Mobile Device"
        seoTitle="Device Security Guide: Harden Computers & Phones | CSFSafe"
        canonicalUrl={canonicalUrl}
        description="Quick guide to hardening your computer and mobile device: updates, screen locks, encryption, USB risks, and backups for Windows, Mac, iOS, and Android."
      ogTitle="Device Security Guide: Harden Computers & Phones | CSFSafe"
      ogDescription="Platform-specific quick setup steps for Windows, Mac, iOS, and Android device security, plus a before/after checklist."
      // ogImage={heroImageSrc}
      // heroImageSrc={heroImageSrc}
      heroImageAlt="Device security hardening for computers and mobile phones"
      publishedLabel="Quick Reference Guide — July 22, 2026"
      showCTA={true}
    >
      <Paragraph>
        Every laptop, phone, and tablet that touches your business is a
        potential entry point for attackers—or a potential loss if it's
        stolen. This guide covers the core hardening steps every device
        needs: updates, screen locks, encryption, physical security, USB
        risks, and backups, with quick setup steps for each major platform.
      </Paragraph>

      <H2>OS Updates: Your First Line of Defense</H2>

      <H3>Why Updates Matter Most</H3>

      <CalloutBox $type="do">
        <strong>Keep Everything Updated</strong>
        <ul>
          <li>
            Enable automatic updates for your operating system, browser, and
            critical applications
          </li>
          <li>
            Don't postpone security updates repeatedly—known vulnerabilities
            get exploited quickly once patches are public
          </li>
          <li>
            Restart devices regularly so pending updates actually install
          </li>
          <li>
            Retire devices that no longer receive security updates from the
            manufacturer
          </li>
        </ul>
      </CalloutBox>

      <H2>Screen Locks & Authentication</H2>

      <CalloutBox $type="tip">
        <strong>Lock It Down</strong>
        <ul>
          <li>
            Set auto-lock to activate after 5 minutes or less on computers,
            immediately on mobile devices
          </li>
          <li>
            Use a PIN, password, or biometric (fingerprint/face) lock—never
            leave a device with no lock screen
          </li>
          <li>
            Use at least a 6-digit PIN on mobile; avoid simple patterns like
            "1234" or "0000"
          </li>
          <li>
            Enable "Find My Device" / remote wipe features on every device
          </li>
        </ul>
      </CalloutBox>

      <H2>Encryption</H2>

      <Paragraph>
        Encryption protects your data if a device is lost or stolen—without
        it, anyone with physical access can pull data directly off the drive.
      </Paragraph>

      <H3>Turn On Full-Disk Encryption</H3>

      <CalloutBox $type="tip">
        <strong>Enable Full-Disk Encryption</strong>
        <ul>
          <li>
            <strong>Windows:</strong> BitLocker (Pro/Enterprise editions) or
            Device Encryption (Home edition, if supported)
          </li>
          <li>
            <strong>Mac:</strong> FileVault
          </li>
          <li>
            <strong>iOS/Android:</strong> Encryption is on by default when a
            passcode is set—make sure a passcode is actually configured
          </li>
          <li>
            Store recovery keys securely (password manager or with IT), not
            on a sticky note
          </li>
        </ul>
      </CalloutBox>

      <H2>Physical Security</H2>

      <CalloutBox $type="warning">
        <strong>Protect the Device Itself</strong>
        <ul>
          <li>Never leave devices unattended in public places, even briefly</li>
          <li>Don't leave laptops visible in a parked car</li>
          <li>Use cable locks for desktop workstations in shared or open offices</li>
          <li>Label devices with contact info (not company name, which can invite social engineering) for return if lost</li>
          <li>Report a lost or stolen device to IT immediately so it can be remotely wiped or locked</li>
        </ul>
      </CalloutBox>

      <H2>USB & External Device Risks</H2>

      <CalloutBox $type="dont">
        <strong>Treat Unknown Devices as Hostile</strong>
        <ul>
          <li>Never plug in a USB drive you found or received unexpectedly—it may be "baiting" malware</li>
          <li>Avoid public USB charging stations; use your own charger and outlet, or a "USB data blocker"</li>
          <li>Disable AutoRun/AutoPlay for removable media on company computers</li>
          <li>Only use company-approved external drives for sensitive data, and encrypt them</li>
          <li>Scan any external device with antivirus software before opening files</li>
        </ul>
      </CalloutBox>

      <H2>Backup Strategy</H2>

      <CalloutBox $type="tip">
        <strong>The 3-2-1 Backup Rule</strong>
        <ul>
          <li><strong>3</strong> copies of important data</li>
          <li><strong>2</strong> different storage types (e.g., cloud + external drive)</li>
          <li><strong>1</strong> copy stored offsite or offline (protects against ransomware reaching connected backups)</li>
          <li>Test restoring from backup periodically—an untested backup is not a reliable backup</li>
          <li>Enable automatic backup for mobile devices (iCloud, Google Backup) for personal device recovery</li>
        </ul>
      </CalloutBox>

      <H2>Platform-Specific Quick Setup Steps</H2>

      <PlatformGrid>
        <PlatformCard>
          <h4>
            <FaWindows /> Windows
          </h4>
          <ol>
            <li>Settings → Update &amp; Security → turn on automatic updates</li>
            <li>Settings → Accounts → Sign-in options → require a PIN/password</li>
            <li>Settings → Privacy &amp; Security → Device encryption → turn on BitLocker</li>
            <li>Settings → Privacy &amp; Security → Windows Security → confirm Defender is active</li>
            <li>Enable "Find My Device" under Update &amp; Security</li>
          </ol>
        </PlatformCard>
        <PlatformCard>
          <h4>
            <FaApple /> Mac
          </h4>
          <ol>
            <li>System Settings → General → Software Update → enable automatic updates</li>
            <li>System Settings → Lock Screen → require password immediately after sleep</li>
            <li>System Settings → Privacy &amp; Security → turn on FileVault</li>
            <li>System Settings → Privacy &amp; Security → confirm firewall is on</li>
            <li>Enable "Find My Mac" via Apple ID settings</li>
          </ol>
        </PlatformCard>
        <PlatformCard>
          <h4>
            <FaApple /> iOS (iPhone/iPad)
          </h4>
          <ol>
            <li>Settings → General → Software Update → enable automatic updates</li>
            <li>Settings → Face ID/Touch ID &amp; Passcode → set a passcode (6+ digits)</li>
            <li>Settings → [Your Name] → Find My → enable Find My iPhone</li>
            <li>Settings → Privacy &amp; Security → review app permissions</li>
            <li>Enable automatic iCloud backup</li>
          </ol>
        </PlatformCard>
        <PlatformCard>
          <h4>
            <FaAndroid /> Android
          </h4>
          <ol>
            <li>Settings → System → System update → check for updates regularly</li>
            <li>Settings → Security → Screen lock → set PIN/pattern/biometric</li>
            <li>Settings → Security → confirm device encryption is enabled</li>
            <li>Settings → Security → Find My Device → turn on</li>
            <li>Settings → System → Backup → enable Google backup</li>
          </ol>
        </PlatformCard>
      </PlatformGrid>

      <H2>Before/After Security Checklist</H2>

      <BeforeAfterGrid>
        <StateCard>
          <h4>Before Hardening (At Risk)</h4>
          <ul>
            <li>No screen lock, or a short PIN like "0000"</li>
            <li>Updates postponed for weeks or months</li>
            <li>No encryption enabled</li>
            <li>No remote wipe capability configured</li>
            <li>Backups nonexistent or untested</li>
            <li>USB drives from unknown sources plugged in freely</li>
          </ul>
        </StateCard>
        <StateCard $after>
          <h4>After Hardening (Protected)</h4>
          <ul>
            <li>Auto-lock enabled with PIN/biometric</li>
            <li>Automatic updates turned on and applied</li>
            <li>Full-disk encryption active</li>
            <li>Find My Device/remote wipe configured and tested</li>
            <li>3-2-1 backup strategy in place and verified</li>
            <li>Unknown USB devices never connected without IT review</li>
          </ul>
        </StateCard>
      </BeforeAfterGrid>

      <H2>Quick Setup Checklist</H2>

      <ChecklistSection>
        <h3>Do This for Every Work Device</h3>
        <ChecklistItem>
          <input type="checkbox" id="item1" />
          <label htmlFor="item1">Automatic OS and app updates enabled</label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item2" />
          <label htmlFor="item2">Screen lock configured with PIN/password/biometric</label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item3" />
          <label htmlFor="item3">Full-disk encryption turned on</label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item4" />
          <label htmlFor="item4">Find My Device / remote wipe enabled and tested</label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item5" />
          <label htmlFor="item5">Antivirus/endpoint protection active and updated</label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item6" />
          <label htmlFor="item6">Backup strategy in place and tested within the last quarter</label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item7" />
          <label htmlFor="item7">No unknown USB devices connected without approval</label>
        </ChecklistItem>
      </ChecklistSection>

      <H2>Related Resources</H2>
      <ul>
        <li>
          <a href={href(LINKS.ARTICLE.SECURE_WINDOWS_LAPTOP)}>
            Securing Your Windows 11 Computer
          </a>
        </li>
        <li>
          <a href={href(LINKS.ARTICLE.SECURE_MACBOOK)}>
            Securing Your MacBook
          </a>
        </li>
        <li>
          <a href={href(LINKS.CYBERSECURITY_FRAMEWORK.BCDR_BASICS)}>
            BCDR Basics: Backup and Disaster Recovery
          </a>
        </li>
        <li>
          <a href={href(LINKS.QUICK_GUIDES.REMOTE_WORK_SECURITY)}>
            Secure Your Home Office &amp; Remote Work
          </a>
        </li>
      </ul>

      <ButtonRow data-print="hide">
        <Button
          as="button"
          type="button"
          onClick={() => window.print()}
          style={{ fontFamily: "inherit" }}
        >
          <FaPrint /> Print or save as PDF
        </Button>
        <Button
          href="https://www.facebook.com/sharer/sharer.php?u=https://csfsafe.com/resources/quick-guides/device-security"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaShare /> Share Guide
        </Button>
      </ButtonRow>
      </ContentLayout>
  );
};

DeviceSecurityGuide.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default DeviceSecurityGuide;
